import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { SearchInput } from './index';

function useIsCompact(breakpoint = 760) {
  const query = `(max-width: ${breakpoint}px)`;
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = event => setCompact(event.matches);
    media.addEventListener('change', update);
    setCompact(media.matches);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return compact;
}

export default function DataTable({
  columns, rows, getRowId = row => row.id, searchKeys = [], searchPlaceholder = 'Search',
  filters = [], pageSize = 10, empty = null, caption, initialSort = null, renderCard,
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(0);
  const compact = useIsCompact();

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || !searchKeys.length) return rows;
    return rows.filter(row => searchKeys.some(key => String(row[key] ?? '').toLowerCase().includes(term)));
  }, [rows, query, searchKeys]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find(item => item.id === sort.id);
    if (!column?.sortValue) return filtered;
    return [...filtered].sort((a, b) => {
      const left = column.sortValue(a);
      const right = column.sortValue(b);
      if (left === right) return 0;
      const order = left > right ? 1 : -1;
      return sort.direction === 'asc' ? order : -order;
    });
  }, [filtered, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const toggleSort = id => {
    setPage(0);
    setSort(current => current?.id === id
      ? { id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { id, direction: 'asc' });
  };

  const hasToolbar = Boolean(searchKeys.length || filters.length);

  return (
    <div className="dt-card is-flush">
      {hasToolbar && (
        <div className="dt-filter-bar">
          {searchKeys.length > 0 && (
            <SearchInput value={query} onChange={event => { setQuery(event.target.value); setPage(0); }} placeholder={searchPlaceholder} />
          )}
          {filters.map(filter => (
            <label className="dt-field" key={filter.id}>
              <span className="sr-only">{filter.label}</span>
              <select className="dt-select" value={filter.value} onChange={event => { filter.onChange(event.target.value); setPage(0); }} aria-label={filter.label}>
                {filter.options.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}

      {!sorted.length ? empty : compact && renderCard ? (
        <div className="dt-card-list">{visible.map(row => <div className="dt-card-row" key={getRowId(row)}>{renderCard(row)}</div>)}</div>
      ) : (
        <div className="dt-table-wrap">
          <table className="dt-table">
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead>
              <tr>
                {columns.map(column => {
                  const active = sort?.id === column.id;
                  const Icon = active && sort.direction === 'desc' ? ChevronDown : ChevronUp;
                  return (
                    <th key={column.id} className={column.sortValue ? 'is-sortable' : ''} style={column.width ? { width: column.width } : undefined}
                      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                      {column.sortValue
                        ? <button type="button" onClick={() => toggleSort(column.id)}>{column.header}<Icon size={13} aria-hidden="true" style={{ opacity: active ? 1 : .35 }} /></button>
                        : column.header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr key={getRowId(row)}>
                  {columns.map(column => <td key={column.id} className={column.align === 'right' ? 'dt-cell-actions' : column.primary ? 'dt-cell-primary' : ''}>{column.render(row)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sorted.length > pageSize && (
        <div className="dt-table-foot">
          <span>Showing {currentPage * pageSize + 1}–{Math.min(sorted.length, (currentPage + 1) * pageSize)} of {sorted.length}</span>
          <div className="dt-table-pager">
            <button type="button" className="dt-btn is-secondary is-sm is-icon" onClick={() => setPage(value => Math.max(0, value - 1))} disabled={currentPage === 0} aria-label="Previous page"><ChevronLeft size={15} /></button>
            <span>Page {currentPage + 1} of {pageCount}</span>
            <button type="button" className="dt-btn is-secondary is-sm is-icon" onClick={() => setPage(value => Math.min(pageCount - 1, value + 1))} disabled={currentPage >= pageCount - 1} aria-label="Next page"><ChevronRight size={15} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
